// The key a take is in, so a band can play in it: how much each pitch class sounds through the take
// (each spectrum's peaks folded into twelve classes, so chords count as well as tunes, on any
// instrument), tuned to the take (it may not be at A 440), against Krumhansl and Kessler's major and
// minor profiles (as src/lib/audio-search/melody.js uses for a tune). Null when no key stands out:
// drums, mostly. Plain math on samples (the first 30 seconds), like the hearing it borrows from.
import { resample, fft } from "../audio-search/transcribe";

const SIZE = 4096, HOP = 2048, LOW = 60, HIGH = 2100;
const MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88], MINOR = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
// A key stands out when its pitch classes fit this well. A relative major and minor share them, so
// the bass (each frame's lowest note: a chord's root, mostly, and the home chord's most of all) has
// a say too, this much of one.
// On 25 takes rendered in 12 keys on six instruments, 24 came out right (from 19 without the bass);
// the miss, a tune with no bass under it, was its relative minor.
const CLEAR = 0.55, BASS = 0.75;
// The keys, spelled as they usually are: { id ("Eb major", as the studio takes it), tonic (pitch
// class), minor, label ("E♭ major") }.
const MAJOR_NAMES = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"], MINOR_NAMES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "G#", "A", "Bb", "B"];
export const keyLabel = (id) => String(id || "").replace(/^([A-G])b/, "$1♭").replace(/^([A-G])#/, "$1♯");
export const KEYS = [false, true].flatMap((minor) => (minor ? MINOR_NAMES : MAJOR_NAMES).map((name, tonic) => {
  const id = `${name} ${minor ? "minor" : "major"}`;
  return { id, tonic, minor, label: keyLabel(id) };
}));
export const keyById = (id) => KEYS.find((key) => key.id === id) || null;
// A key signature as TrueChops writes it (a score's `keySig`: "Eb", "F#"), as the pitch class of
// its major key.
const SIGNATURES = { Cb: 11, Gb: 6, Db: 1, Ab: 8, Eb: 3, Bb: 10, F: 5, C: 0, G: 7, D: 2, A: 9, E: 4, B: 11, "F#": 6, "C#": 1 };

// How much each pitch class sounds, frame by frame through the take (each frame counts alike, so
// loud bars don't decide it), and the take's tuning in cents from A 440.
export function chroma(input, sampleRate) {
  const { samples, rate } = resample(input, sampleRate), frames = Math.max(0, Math.floor((samples.length - SIZE) / HOP) + 1);
  const window = Float32Array.from({ length: SIZE }, (_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / SIZE));
  const from = Math.ceil((LOW * SIZE) / rate), to = Math.floor((HIGH * SIZE) / rate), re = new Float32Array(SIZE), im = new Float32Array(SIZE);
  const peaks = [], loudness = new Float32Array(frames);
  for (let frame = 0; frame < frames; frame++) {
    for (let i = 0; i < SIZE; i++) { re[i] = samples[frame * HOP + i] * window[i]; im[i] = 0; }
    fft(re, im);
    const magnitude = new Float32Array(to + 2);
    let top = 0;
    for (let k = from - 1; k <= to + 1; k++) { magnitude[k] = Math.hypot(re[k], im[k]); top = Math.max(top, magnitude[k]); loudness[frame] += magnitude[k] * magnitude[k]; }
    const found = [];
    for (let k = from; k <= to; k++) {
      const value = magnitude[k];
      if (value <= magnitude[k - 1] || value < magnitude[k + 1] || value < 0.03 * top) continue;
      // Between bins, by the parabola through the peak and its neighbours.
      const a = magnitude[k - 1], c = magnitude[k + 1], shift = (a - c) / (2 * (a - 2 * value + c) || 1);
      found.push({ frequency: ((k + shift) * rate) / SIZE, amount: value });
    }
    // Notes from the bottom up: each takes its overtones away from the peaks above it (as much as
    // it has itself, less up the series), so a D's twelfth and seventeenth (an A, an F♯) aren't
    // counted as notes of their own. What's left of a peak counts.
    for (let i = 0; i < found.length; i++) {
      const note = found[i];
      if (note.amount <= 0) continue;
      for (let j = i + 1; j < found.length; j++) {
        const harmonic = found[j].frequency / note.frequency, h = Math.round(harmonic);
        if (h >= 2 && h <= 8 && Math.abs(harmonic / h - 1) < 0.03) found[j].amount -= note.amount * (h <= 4 ? 1 : 0.6);
      }
    }
    peaks.push(found.filter((peak) => peak.amount > 0.03 * top).map((peak) => ({ pitch: 69 + 12 * Math.log2(peak.frequency / 440), weight: Math.log1p(peak.amount / (0.03 * top)) })));
  }
  // Quiet frames (the count-in, a pause) don't count.
  const loudest = Math.max(0, ...loudness);
  let x = 0, y = 0;
  peaks.forEach((found, frame) => { if (loudness[frame] < 0.01 * loudest) found.length = 0; for (const { pitch, weight } of found) { const angle = 2 * Math.PI * (pitch - Math.round(pitch)); x += weight * Math.cos(angle); y += weight * Math.sin(angle); } });
  const tuning = x || y ? (Math.atan2(y, x) / (2 * Math.PI)) * 100 : 0, classes = new Float64Array(12), bass = new Float64Array(12);
  for (const found of peaks) {
    const frame = new Float64Array(12), strongest = Math.max(0, ...found.map((peak) => peak.weight));
    let sum = 0, lowest = null;
    for (const { pitch, weight } of found) {
      const tuned = pitch - tuning / 100, nearest = Math.round(tuned), closeness = Math.max(0, 1 - 2 * Math.abs(tuned - nearest)), pitchClass = ((nearest % 12) + 12) % 12;
      frame[pitchClass] += weight * closeness; sum += weight * closeness;
      if (lowest == null && weight >= 0.25 * strongest && closeness > 0.5) lowest = pitchClass;
    }
    if (sum > 0) for (let i = 0; i < 12; i++) classes[i] += frame[i] / sum;
    if (lowest != null) bass[lowest] += 1;
  }
  return { classes, bass, tuning };
}

function correlation(classes, profile, tonic) {
  const b = Array.from({ length: 12 }, (_, i) => profile[(i - tonic + 12) % 12]), ma = classes.reduce((sum, value) => sum + value, 0) / 12, mb = b.reduce((sum, value) => sum + value, 0) / 12;
  let product = 0, da = 0, db = 0;
  for (let i = 0; i < 12; i++) { product += (classes[i] - ma) * (b[i] - mb); da += (classes[i] - ma) ** 2; db += (b[i] - mb) ** 2; }
  return da && db ? product / Math.sqrt(da * db) : 0;
}

// The key a take is in: one of KEYS, with `fit` (how well, from -1 to 1) and the take's tuning; null
// when none stands out. `signature` (a score's keySig, when the take goes with an idea) narrows it to
// that signature's major key and its relative minor.
export function keyOf(samples, rate, { signature = null } = {}) {
  const { classes, bass, tuning } = chroma(samples, rate);
  const best = bestKey(classes, bass, signature);
  return best && best.fit >= CLEAR ? { ...best, tuning: Math.round(tuning) } : null;
}

// The key notation is in, from its notes (scoreEvents in graph/midi.js; drums, on channel 10, have
// none): each pitch class by how long it sounds, each onset's lowest note as the bass. Null without
// notes; otherwise the best fit, however clear (notation has no noise to mistake for a key).
export function keyOfNotes(events, { signature = null } = {}) {
  const classes = new Array(12).fill(0), bass = new Array(12).fill(0), lowest = new Map();
  for (const event of events) {
    if (event.channel === 9) continue;
    classes[event.pitch % 12] += Math.max(0.25, event.length || 0);
    const at = Math.round(event.at * 4);
    if (!lowest.has(at) || event.pitch < lowest.get(at)) lowest.set(at, event.pitch);
  }
  for (const pitch of lowest.values()) bass[pitch % 12] += 1;
  return bestKey(classes, bass, signature);
}

function bestKey(classes, bass, signature) {
  if (!classes.some((value) => value > 0)) return null;
  const major = SIGNATURES[signature], allowed = major == null ? KEYS : KEYS.filter((key) => key.tonic === (key.minor ? (major + 9) % 12 : major));
  let best = null;
  for (const key of allowed) {
    const profile = key.minor ? MINOR : MAJOR, fit = correlation(classes, profile, key.tonic), score = fit + BASS * correlation(bass, profile, key.tonic);
    if (!best || score > best.score) best = { ...key, fit, score };
  }
  return best && { id: best.id, tonic: best.tonic, minor: best.minor, label: best.label, fit: best.fit };
}
